<template>
    <div itemscope itemtype="https://schema.org/Thing" class="stoffe-image-gallery">
        <div v-if="showThumbs && carouselImages.length > 1" class="stoffe-gallery__thumbnails">
            <button
                @click="scrollThumbs('up')"
                class="stoffe-gallery__nav stoffe-gallery__nav--up"
                :disabled="thumbScrollPos <= 0">
                <i class="fa fa-chevron-up"></i>
            </button>

            <div ref="thumbContainer" class="stoffe-gallery__thumb-container">
                <div
                    v-for="(imagePreview, index) in carouselImages"
                    :key="index"
                    @click="goTo(index)"
                    class="stoffe-gallery__thumb"
                    :class="{ 'stoffe-gallery__thumb--active': currentItem === index }">
                    <lazy-img
                        :alt="getAltText(imagePreview)"
                        :image-url="imagePreview.url"
                        :title="getImageName(imagePreview)"
                        :width="getImageWidth(imagePreview)"
                        :height="getImageHeight(imagePreview)"
                        picture-class="stoffe-gallery__thumb-img" />
                </div>
            </div>

            <button
                @click="scrollThumbs('down')"
                class="stoffe-gallery__nav stoffe-gallery__nav--down"
                :disabled="thumbScrollPos >= maxThumbScroll">
                <i class="fa fa-chevron-down"></i>
            </button>
        </div><div class="stoffe-gallery__main">
            <add-to-wish-list :variation-id="currentVariation.variation.id" :show-text="false"></add-to-wish-list>
            <div ref="single" class="single-carousel owl-carousel owl-theme owl-single-item">
                <div v-for="(image, index) in singleImages" :key="index" class="prop-1-1">
                    <a :href="image.url" :data-lightbox="`single-item-image${_uid}`">
                        <lazy-img
                            v-if="shouldLoadImage(index)"
                            :alt="getAltText(image)"
                            :image-url="image.url"
                            :title="getImageName(image)"
                            :width="getImageWidth(image)"
                            :height="getImageHeight(image)" />
                        <div
                            v-else
                            class="stoffe-gallery__placeholder"
                            :style="{
                                width: getImageWidth(image) + 'px',
                                height: getImageHeight(image) + 'px'
                            }">
                        </div>
                    </a>
                </div>
            </div>

            <div v-if="!initialized && singleImages.length" class="single-carousel owl-carousel owl-loaded owl-theme owl-single-item">
                <div class="prop-1-1">
                    <lazy-img
                        :alt="getAltText(singleImages[0])"
                        :image-url="singleImages[0].url"
                        :title="getImageName(singleImages[0])"
                        :width="getImageWidth(singleImages[0])"
                        :height="getImageHeight(singleImages[0])"
                        picture-class="owl-placeholder" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { isNullOrUndefined } from "../helper/utils";

export default {
    name: "item-image-carousel",

    props: {
        maxQuantity:
        {
            type: Number,
            default: 10
        },
        imageUrlAccessor:
        {
            type: String,
            default: "url"
        },
        showThumbs:
        {
            type: Boolean,
            default: true
        },
        showDots:
        {
            type: Boolean,
            default: true
        },
        animationStyle:
        {
            type: String,
            default: "standard"
        },
        pluginPath:
        {
            type: String,
            default: ""
        }
    },

    inject: {
        itemId: {
            default: null
        }
    },

    data()
    {
        return {
            currentItem: 0,
            initialized: false,
            thumbScrollPos: 0,
            maxThumbScroll: 0,
            thumbHeight: 100,
            loadedImages: new Set([0])
        };
    },

    computed:
    {
        currentVariation()
        {
            return this.$store.getters[`${this.itemId}/currentItemVariation`]
        },

        carouselImages()
        {
            // Filterung/Sortierung nach position passiert zentral im itemImages-Filter
            return this.$options.filters.itemImages(
                    this.currentVariation.images,
                    "urlPreview"
                )
                .slice(0, this.maxQuantity);
        },

        singleImages()
        {
            return this.$options.filters.itemImages(
                    this.currentVariation.images,
                    this.imageUrlAccessor
            )
                .slice(0, this.maxQuantity);
        }
    },

    watch: {
        currentVariation:
        {
            handler(val, oldVal)
            {
                if (val !== oldVal)
                {
                    setTimeout(() =>
                    {
                        this.reInitialize();
                    }, 1);
                }
            },
            deep: true
        }
    },

    mounted()
    {
        this.$nextTick(() =>
        {
            this.loadLightbox().then(() =>
                {
                    this.initCarousel();
                    this.calculateThumbScroll();
                })
                .catch(event =>
                {
                    console.log("error while loading lightbox", event);
                });
        });

        window.addEventListener('resize', this.calculateThumbScroll);
    },

    beforeDestroy()
    {
        window.removeEventListener('resize', this.calculateThumbScroll);
    },

    methods:
    {
        getImageCount()
        {
            return this.carouselImages.length > this.maxQuantity ? this.maxQuantity : this.carouselImages.length;
        },

        reInitialize()
        {
            const $owl = $(this.$refs.single);

            $owl.trigger("destroy.owl.carousel");
            $owl.html($owl.find(".owl-stage-outer").html()).removeClass("owl-loaded");
            $owl.find(".owl-item").remove();

            this.loadedImages = new Set([0]);
            this.currentItem = 0;
            this.thumbScrollPos = 0;

            this.initCarousel();
            this.calculateThumbScroll();
        },

        initCarousel()
        {
            const imageCount = this.getImageCount();
            const carouselSettings = {
                autoHeight       : true,
                dots             : this.showDots,
                items            : 1,
                lazyLoad         : true,
                rewind           : true,
                margin           : 10,
                mouseDrag        : imageCount > 1,
                nav              : imageCount > 1,
                navClass         : [
                    "owl-single-item-nav left carousel-control",
                    "owl-single-item-nav right carousel-control"
                ],
                navContainerClass: "",
                navText          : [
                    "<i class=\"owl-single-item-control fa fa-chevron-left\" aria-hidden=\"true\"></i>",
                    "<i class=\"owl-single-item-control fa fa-chevron-right\" aria-hidden=\"true\"></i>"
                ],
                smartSpeed       : 350,
                onChanged: event =>
                {
                    this.currentItem = event.page.index;
                    this.ensureThumbVisible(event.page.index);
                    this.preloadAdjacentImages(event.page.index);
                },
                onInitialized: event =>
                {
                    this.initialized = true;
                    this.preloadAdjacentImages(0);
                }
            };

            if (this.animationStyle !== "standard")
            {
                carouselSettings.animateOut = this.animationStyle;
            }

            $(this.$refs.single).owlCarousel(carouselSettings);

            if (!isNullOrUndefined(window.lightbox))
            {
                lightbox.option({
                    wrapAround: true,
                    albumLabel: this.$translate("Ceres::Template.singleItemLightboxImageShown")
                });
            }
        },

        scrollThumbs(direction)
        {
            const container = this.$refs.thumbContainer;
            if (!container) return;

            const scrollAmount = this.thumbHeight + 10;

            if (direction === 'up')
            {
                this.thumbScrollPos = Math.max(0, this.thumbScrollPos - scrollAmount);
            }
            else
            {
                this.thumbScrollPos = Math.min(this.maxThumbScroll, this.thumbScrollPos + scrollAmount);
            }

            container.scrollTop = this.thumbScrollPos;
        },

        calculateThumbScroll()
        {
            this.$nextTick(() =>
            {
                const container = this.$refs.thumbContainer;
                if (!container) return;

                const containerHeight = container.offsetHeight;
                const scrollHeight = container.scrollHeight;
                this.maxThumbScroll = Math.max(0, scrollHeight - containerHeight);
            });
        },

        ensureThumbVisible(index)
        {
            this.$nextTick(() =>
            {
                const container = this.$refs.thumbContainer;
                if (!container) return;

                const thumbElements = container.children;
                if (!thumbElements[index]) return;

                const thumbElement = thumbElements[index];

                // Check if horizontal scrolling (Tablet mode)
                const isHorizontal = container.scrollWidth > container.clientWidth &&
                                   container.scrollHeight <= container.clientHeight;

                if (isHorizontal)
                {
                    // Horizontal scrolling (Tablet: 768-991px) - Zentrierung
                    const thumbLeft = thumbElement.offsetLeft;
                    const thumbWidth = thumbElement.offsetWidth;
                    const containerWidth = container.offsetWidth;
                    const targetScroll = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);

                    container.scrollTo({
                        left: Math.max(0, targetScroll),
                        behavior: 'smooth'
                    });
                }
                else
                {
                    // Vertical scrolling (Desktop: >991px) - Zentrierung mit smooth scroll
                    const thumbTop = thumbElement.offsetTop;
                    const thumbHeight = thumbElement.offsetHeight;
                    const containerHeight = container.offsetHeight;
                    const targetScroll = thumbTop - (containerHeight / 2) + (thumbHeight / 2);

                    container.scrollTo({
                        top: Math.max(0, targetScroll),
                        behavior: 'smooth'
                    });

                    this.thumbScrollPos = Math.max(0, targetScroll);
                }
            });
        },

        goTo(index)
        {
            const $owl = $(this.$refs.single);

            $owl.trigger("to.owl.carousel", [
                index,
                350
            ]);
        },

        shouldLoadImage(index)
        {
            return this.loadedImages.has(index);
        },

        preloadAdjacentImages(currentIndex)
        {
            const maxIndex = this.singleImages.length - 1;

            // Aktuelles Bild
            this.loadedImages.add(currentIndex);

            // Vorheriges Bild
            if (currentIndex > 0)
            {
                this.loadedImages.add(currentIndex - 1);
            }

            // Nächstes Bild
            if (currentIndex < maxIndex)
            {
                this.loadedImages.add(currentIndex + 1);
            }

            // Force Update damit Vue die Änderungen erkennt
            this.$forceUpdate();
        },

        getAltText(image)
        {
            return image && image.alternate ? image.alternate : this.$options.filters.itemName(this.currentVariation);
        },

        getImageName(image)
        {
            return image && image.name ? image.name : this.$options.filters.itemName(this.currentVariation);
        },

        getImageWidth(image)
        {
          return image && image.width ? image.width : this.$options.filters.itemImageWidth(this.carouselImages);
        },

        getImageHeight(image)
        {
          return image && image.height ? image.height : this.$options.filters.itemImageHeight(this.carouselImages);
        },

        loadLightbox()
        {
            return new Promise((resolve, reject) =>
            {
                const script = document.querySelector("script#lightboxscript");

                if (!isNullOrUndefined(script))
                {
                    resolve();
                }
                else
                {
                    const script = document.createElement("script");

                    script.type = "text/javascript";
                    script.id = "lightboxscript";
                    script.src = `${ this.pluginPath }/js/dist/lightbox.min.js`;

                    script.addEventListener("load", () => resolve(), false);
                    script.addEventListener("error", event => reject(event), false);

                    document.body.appendChild(script);
                }
            });
        }
    }
}
</script>
